import {
  Button as MaterialButton,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PublishIcon from '@material-ui/icons/Publish';
import cn from 'classnames';
import { useSnackbar } from 'notistack';
import { DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '.';
import enrichAPI, {
  EnrichedMentorResponse,
  EnrichedVentureResponse,
} from '../../api/enrich';
import { TenantId } from '../../api/tenants/Tenant';
import { COLORS } from '../../theme/variables';
import { FILE_SIZE_LIMIT, FILE_SIZE_LIMIT_MB } from '../../utils/form';
import ModalV2 from './modal-v2';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  description: {
    marginBottom: 24,
    textAlign: 'center',
    color: '#616161',
    fontSize: 14,
    lineHeight: 1.5,
  },
  dropZone: {
    width: '100%',
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #bdbdbd',
    borderRadius: 8,
    backgroundColor: '#fafafa',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, background-color 0.2s ease',
    padding: 24,
    boxSizing: 'border-box',
    textAlign: 'center',
    '&:hover': {
      borderColor: '#9e9e9e',
    },
  },
  dropZoneDragOver: {
    borderColor: '#1976d2',
    backgroundColor: '#e3f2fd',
  },
  dropZoneLabel: {
    color: '#9e9e9e',
    fontSize: 14,
    lineHeight: 1.5,
    marginBottom: 16,
  },
  selectFileButton: {
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    marginTop: 56,
  },
  errorText: {
    color: '#f44336',
    fontSize: 13,
    marginTop: 8,
    textAlign: 'center',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
    padding: '8px 12px',
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    width: '100%',
    boxSizing: 'border-box',
  },
  fileName: {
    flex: 1,
    fontSize: 14,
    color: '#424242',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeFileButton: {
    padding: 4,
  },
  loadingOverlay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 180,
    width: '100%',
    gap: 16,
  },
  loadingText: {
    fontSize: 14,
    color: '#616161',
  },
});

const ALLOWED_EXTENSIONS = ['.txt', '.pdf', '.html', '.docx'];

const ACCEPTED_MIME_TYPES =
  'text/plain,application/pdf,text/html,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function getFileExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex === -1) return '';
  return filename.slice(dotIndex).toLowerCase();
}

function validateFile(file: File): string | null {
  const extension = getFileExtension(file.name);
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return `Invalid file type. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`;
  }
  if (file.size > FILE_SIZE_LIMIT) {
    return `File size exceeds ${FILE_SIZE_LIMIT_MB}MB limit`;
  }
  return null;
}

export interface PrefillModalProps {
  open: boolean;
  onClose: () => void;
  onPrefillComplete: (
    data: EnrichedMentorResponse | EnrichedVentureResponse,
  ) => void;
  tenantId: TenantId;
  wizardType: 'mentor' | 'venture';
}

function PrefillModal({
  open,
  onClose,
  onPrefillComplete,
  tenantId,
  wizardType,
}: PrefillModalProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const scrollEl = document.getElementById('wizard-scroll');
    if (scrollEl) {
      scrollEl.style.overflow = 'hidden';
      return () => {
        scrollEl.style.overflow = 'auto';
      };
    }
  }, [open]);

  const handleFileSelect = useCallback((selectedFile: File) => {
    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setFile(selectedFile);
  }, []);

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        handleFileSelect(droppedFile);
      }
    },
    [handleFileSelect],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        handleFileSelect(selectedFile);
      }
      e.target.value = '';
    },
    [handleFileSelect],
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!file) return;

    setIsUploading(true);
    try {
      const response =
        wizardType === 'mentor'
          ? await enrichAPI.enrichMentorFromFile(tenantId, file)
          : await enrichAPI.enrichVentureFromFile(tenantId, file);

      onPrefillComplete(response);
      onClose();
    } catch (_e) {
      enqueueSnackbar('Unfortunately we were not able to pre-fill the fields', {
        variant: 'error',
      });
      onClose();
    } finally {
      setIsUploading(false);
    }
  }, [file, wizardType, tenantId, onPrefillComplete, onClose, enqueueSnackbar]);

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      titleProps={{ title: 'Prefill the Information' }}
      width='700px'
      data-testid='prefill-modal'
    >
      <div className={classes.content}>
        {!isUploading && (
          <p className={classes.description}>
            You can upload any text files (.txt, .pdf, .html, .docx up to{' '}
            {FILE_SIZE_LIMIT_MB}MB) that contain the data we can use to help you
            pre-fill values in this application
          </p>
        )}

        {isUploading ? (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={40} />
            <span className={classes.loadingText}>Processing your file...</span>
          </div>
        ) : (
          <>
            <div
              className={cn(classes.dropZone, {
                [classes.dropZoneDragOver]: isDragOver,
              })}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid='prefill-modal-drop-zone'
            >
              <span className={classes.dropZoneLabel}>
                Please drag and drop here any .txt, .pdf, .html, .docx files
              </span>
              <MaterialButton
                variant='outlined'
                color='primary'
                className={classes.selectFileButton}
                onClick={handleUploadClick}
                data-testid='prefill-modal-select-file-btn'
              >
                Select File
              </MaterialButton>
            </div>

            {error && (
              <p className={classes.errorText} data-testid='prefill-modal-error'>
                {error}
              </p>
            )}

            {file && (
              <div
                className={classes.fileInfo}
                data-testid='prefill-modal-file-info'
              >
                <InsertDriveFileIcon fontSize='small' color='action' />
                <span className={classes.fileName}>{file.name}</span>
                <IconButton
                  className={classes.removeFileButton}
                  onClick={handleRemoveFile}
                  size='small'
                  aria-label='Remove file'
                  data-testid='prefill-modal-remove-file'
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
              </div>
            )}

            <div className={classes.buttonRow}>
              <MaterialButton
                variant='contained'
                color='primary'
                startIcon={<PublishIcon />}
                onClick={handleSubmit}
                disabled={!file || isUploading}
                data-testid='prefill-modal-upload-btn'
              >
                Upload
              </MaterialButton>
              <Button
                variant='outlined'
                onClick={onClose}
                data-testid='prefill-modal-close-btn'
              >
                Close
              </Button>
              <input
                ref={fileInputRef}
                type='file'
                accept={ACCEPTED_MIME_TYPES}
                hidden
                onChange={handleInputChange}
                data-testid='prefill-modal-file-input'
              />
            </div>
          </>
        )}
      </div>
    </ModalV2>
  );
}

export default PrefillModal;
